import { selectQuotationClosedBoxStructure } from "app/containers/WorkOrder/redux/selector";
import { actions } from "app/containers/WorkOrder/redux/slice";
import {
  AddOnItemFormValue,
  QuotationClosedBoxCardKey,
} from "app/containers/WorkOrder/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  CLOSED_FRONT_WALL_OPTIONS,
  CLOSED_HYDRAULIC_TON_OPTIONS,
  CLOSED_REAR_FRAME_OPTIONS,
  CLOSED_REAR_OPENING_OPTIONS,
  CLOSED_ROOF_TYPE_OPTIONS,
  CLOSED_SIDE_FLAP_OPTIONS,
} from "../../../data";
import { formKey, NOTE, TYPE_KEY } from "../../../hooks/useQuoteBoxStructure";
import { QuoteSelectOption } from "../../../types";
import {
  FieldsRow,
  StructureCard,
} from "../OpenBoxQuotes/BoxStructureFields";
import { NoteField, NumberField, SelectField } from "../OpenBoxQuotes/FormFields";

export const closedKey = (suffix: string) => formKey(`CLOSED_BOX.${suffix}`);

export const ROOF_TYPE = CLOSED_ROOF_TYPE_OPTIONS;
export const FRONT_WALL = CLOSED_FRONT_WALL_OPTIONS;
export const REAR_FRAME = CLOSED_REAR_FRAME_OPTIONS;
export const REAR_OPENING = CLOSED_REAR_OPENING_OPTIONS;
export const HYDRAULIC_TON = CLOSED_HYDRAULIC_TON_OPTIONS;
export const SIDE_FLAP = CLOSED_SIDE_FLAP_OPTIONS;

const CARD_TITLES: Record<QuotationClosedBoxCardKey, string> = {
  roofType: "ROOF_TYPE",
  frontWall: "FRONT_WALL",
  rearFrame: "REAR_FRAME",
  frontWallReinforcement: "FRONT_WALL_REINFORCEMENT",
  sideDoorOpenings: "SIDE_DOOR_OPENINGS",
  rearOpening: "REAR_OPENING",
  hydraulicSide: "HYDRAULIC_SIDE",
  sideCompletionFlap: "SIDE_COMPLETION_FLAP",
};

export const useClosedBoxStructure = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectQuotationClosedBoxStructure);
  const patch = (
    key: QuotationClosedBoxCardKey,
    value: Partial<AddOnItemFormValue>
  ) => dispatch(actions.updateQuotationClosedBoxStructure({ key, value }));

  return { data, patch };
};

const titleKey = (cardKey: QuotationClosedBoxCardKey) =>
  closedKey(`CARDS.${CARD_TITLES[cardKey]}`);

export const TypeCard = ({
  cardKey,
  options,
  selectLabelKey = TYPE_KEY,
}: {
  cardKey: QuotationClosedBoxCardKey;
  options: QuoteSelectOption[];
  selectLabelKey?: string;
}) => {
  const { data, patch } = useClosedBoxStructure();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey(cardKey)}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <SelectField
        labelKey={selectLabelKey}
        value={item.selectedOption}
        options={options}
        onChange={(selectedOption) => patch(cardKey, { selectedOption })}
      />
    </StructureCard>
  );
};

export const NoteCard = ({
  cardKey,
}: {
  cardKey: QuotationClosedBoxCardKey;
}) => {
  const { data, patch } = useClosedBoxStructure();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey(cardKey)}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <NoteField
        labelKey={NOTE}
        placeholderKey={NOTE}
        value={item.note}
        onChange={(note) => patch(cardKey, { note })}
      />
    </StructureCard>
  );
};

export const SideDoorOpeningsCard = () => {
  const { t } = useTranslation();
  const { data, patch } = useClosedBoxStructure();
  const item = data.sideDoorOpenings;
  const note = item.note.startsWith("QUOTES.") ? t(item.note) : item.note;

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey("sideDoorOpenings")}
      onToggle={(enabled) => patch("sideDoorOpenings", { enabled })}
    >
      <FieldsRow>
        <NumberField
          labelKey={closedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) => patch("sideDoorOpenings", { quantity })}
          compact
        />
        <NoteField
          labelKey={closedKey("FIELDS.NOTE_LOCATION")}
          placeholderKey={closedKey("FIELDS.NOTE_LOCATION")}
          value={note}
          onChange={(nextNote) => patch("sideDoorOpenings", { note: nextNote })}
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};
